import moment from "moment";
import * as XLSXStyle from "xlsx-js-style";
import {
  border,
  headerStyle,
  textStyle,
  moneyStyle,
  celdaTexto,
  celdaMoneda,
  nombreEmpresa,
} from "../../../../Components/ExcelEstilos";
import {
  RESALTES,
  construirHojaTabla,
  descargarLibro,
  limpiarNombreArchivo,
  texto,
  identificador,
  moneda,
  fecha as fechaCelda,
} from "../../../../Components/ExcelTablaReporte";
import { ETIQUETA_ORIGEN, textoCuentas } from "./columnasFlujo";

/*
  Excel del Flujo De Efectivo Diario.

  Hoja 1 replica el acomodo del flujo que Proaliv llevaba a mano: columna A con los
  conceptos, una columna por dia (o semana / mes), cada bloque (INGRESOS, EGRESOS, IVA)
  con su doble renglon de encabezado (dia y fecha) y renglones en blanco entre grupos.
  Los totales y el remanente van como FORMULAS con su valor ya calculado: asi pueden
  seguir capturando a mano sobre el archivo, como hacian con el suyo. El saldo inicial
  queda en una celda propia arriba y el remanente lo referencia.

  Hoja 2 es el detalle de los movimientos de cada celda.

  Colores: paleta Biwo (ver ExcelEstilos). Real en turquesa, hoy en teal oscuro y lo
  proyectado en turquesa claro, para que se distinga a simple vista.
*/

const TITULO = "Flujo de Efectivo Diario";

const estiloTexto = { font: { bold: true, sz: 13 } };
const estiloSubtitulo = { font: { bold: true, sz: 11, color: { rgb: "005F6B" } } };
const estiloMeta = { font: { sz: 9, color: { rgb: "8C8C8C" } } };

const bandaTitulo = {
  font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "007A8A" } },
  alignment: { vertical: "center" },
  border,
};

const ENCABEZADO_POR_TIPO = {
  real: headerStyle,
  hoy: { ...headerStyle, fill: { fgColor: { rgb: "007A8A" } } },
  proyectado: {
    ...headerStyle,
    font: { bold: true, sz: 10, color: { rgb: "005F6B" } },
    fill: { fgColor: { rgb: "D6F4F8" } },
  },
};

const estiloSeccion = {
  ...textStyle,
  font: { bold: true, color: { rgb: "005F6B" } },
  fill: { fgColor: { rgb: "F0FEFF" } },
};

const informativoTexto = { ...textStyle, font: { italic: true, color: { rgb: "8C8C8C" } } };
const informativoDinero = { ...moneyStyle, font: { italic: true, color: { rgb: "8C8C8C" } } };

const conResalte = (base, resalte) => ({ ...base, ...RESALTES[resalte] });

// Totales que cierran el flujo: un tono mas fuerte que los subtotales de cada grupo.
const TOTALES_FUERTES = new Set(["tot_ingresos", "tot_egresos", "iva_resumen"]);
const SALDOS_FUERTES = new Set(["rem_remanente", "rem_saldoInicial", "rem_disponible"]);

const estilosDeRenglon = (renglon) => {
  if (renglon.tipo === "informativo") return { etiqueta: informativoTexto, dinero: informativoDinero };
  if (renglon.tipo === "total") {
    const resalte = TOTALES_FUERTES.has(renglon.clave) ? "utilidad" : "subtotal";
    return { etiqueta: conResalte(textStyle, resalte), dinero: conResalte(moneyStyle, resalte) };
  }
  if (renglon.tipo === "saldo") {
    if (SALDOS_FUERTES.has(renglon.clave)) {
      return { etiqueta: conResalte(textStyle, "utilidad"), dinero: conResalte(moneyStyle, "utilidad") };
    }
    return { etiqueta: { ...textStyle, font: { bold: true } }, dinero: { ...moneyStyle, font: { bold: true } } };
  }
  return { etiqueta: textStyle, dinero: moneyStyle };
};

// Fecha-dia como numero de serie de Excel (dias desde 1899-12-30). No se manda un Date:
// la libreria lo convierte con la zona del navegador y en CDMX lo corre unos segundos por
// el desfase de 1899, asi que la celda salia con hora 05:59:24.
const fechaExcel = (clave, estilo) => {
  const [anio, mes, dia] = clave.split("-").map(Number);
  return {
    v: Date.UTC(anio, mes - 1, dia) / 86400000 + 25569,
    t: "n",
    z: "dd/mm/yyyy",
    s: { ...estilo, numFmt: "dd/mm/yyyy" },
  };
};

const celdaFormula = (formula, valor, estilo) => ({
  f: formula,
  v: Number(valor) || 0,
  t: "n",
  s: estilo,
});

// Igual que la plantilla de Proaliv: A = Cuenta, B = Observaciones, C = Concepto y los
// dias desde la D.
const COLUMNAS_FIJAS = 3;

// Columna de Excel de la columna `indice` del reporte.
const letra = (indice) => XLSXStyle.utils.encode_col(indice + COLUMNAS_FIJAS);

// "SUM(D7:D15,D18)" cuando todo suma; "D10-D11" cuando hay restas.
const formulaDeTotal = (componentes, filaDe, col) => {
  const refs = componentes.map((componente) => {
    const resta = componente.startsWith("-");
    return { resta, fila: filaDe[resta ? componente.slice(1) : componente] };
  });
  if (refs.some((r) => !r.fila)) return null;
  if (refs.some((r) => r.resta)) {
    return refs
      .map((r, i) => `${r.resta ? "-" : i === 0 ? "" : "+"}${col}${r.fila}`)
      .join("");
  }
  const filas = refs.map((r) => r.fila).sort((a, b) => a - b);
  const rangos = [];
  filas.forEach((fila) => {
    const ultimo = rangos[rangos.length - 1];
    if (ultimo && fila === ultimo.fin + 1) ultimo.fin = fila;
    else rangos.push({ inicio: fila, fin: fila });
  });
  return `SUM(${rangos
    .map((r) => (r.inicio === r.fin ? `${col}${r.inicio}` : `${col}${r.inicio}:${col}${r.fin}`))
    .join(",")})`;
};

// La cuenta va como texto explicito: "105" o "201-01-002" no deben volverse numeros.
const celdaCuenta = (texto, estilo) => ({ v: texto || "", t: "s", z: "@", s: { ...estilo, numFmt: "@" } });

const construirHojaFlujo = ({ columnas, renglones, saldoInicial, empresaNombre, descripcionFiltros }) => {
  const aoa = [];
  const filaDe = {};
  const agregar = (fila, clave) => {
    aoa.push(fila);
    if (clave) filaDe[clave] = aoa.length;
  };
  const vacias = (estilo) => columnas.map(() => (estilo ? { v: "", s: estilo } : null));

  agregar([celdaTexto(empresaNombre || "", estiloTexto)]);
  agregar([celdaTexto(TITULO, estiloSubtitulo)]);
  agregar([celdaTexto(descripcionFiltros || "", estiloMeta)]);
  agregar([
    celdaTexto(
      `Generado ${moment().format("DD/MM/YYYY HH:mm")}. Encabezado turquesa = real, ` +
        `teal oscuro = hoy, turquesa claro = proyeccion de cuentas por cobrar y por pagar.`,
      estiloMeta
    ),
  ]);
  // El saldo inicial en su propia celda (D5, debajo del primer dia): el remanente lo
  // referencia, asi que si lo cambian a mano todo el flujo se recalcula.
  agregar(
    [
      null,
      null,
      celdaTexto("Saldo inicial en bancos", { ...textStyle, font: { bold: true } }),
      celdaMoneda(saldoInicial || 0, { ...moneyStyle, font: { bold: true } }),
    ],
    "saldoInicialCelda"
  );

  renglones.forEach((renglon) => {
    // Renglon en blanco antes de cada bloque y de cada grupo, como en el Excel original.
    if (renglon.tipo === "seccion" || renglon.tipo === "titulo") agregar([]);
    if (renglon.tipo === "titulo") {
      agregar(
        [
          celdaTexto("", bandaTitulo),
          celdaTexto("", bandaTitulo),
          celdaTexto(renglon.etiqueta, bandaTitulo),
          ...columnas.map((c) => celdaTexto(c.titulo, ENCABEZADO_POR_TIPO[c.tipo])),
        ],
        renglon.clave
      );
      agregar([
        celdaTexto("Cuenta", headerStyle),
        celdaTexto("Observaciones", headerStyle),
        celdaTexto("", bandaTitulo),
        ...columnas.map((c) =>
          c.fecha ? fechaExcel(c.fecha, ENCABEZADO_POR_TIPO[c.tipo]) : celdaTexto(c.subtitulo, ENCABEZADO_POR_TIPO[c.tipo])
        ),
      ]);
      return;
    }
    if (renglon.tipo === "seccion") {
      agregar(
        [
          celdaCuenta(textoCuentas(renglon), estiloSeccion),
          celdaTexto(renglon.observaciones || "", estiloSeccion),
          celdaTexto(renglon.etiqueta, estiloSeccion),
          ...vacias(estiloSeccion),
        ],
        renglon.clave
      );
      return;
    }
    // Se reserva la fila; las celdas se llenan en una segunda pasada, cuando ya se
    // sabe en que fila quedo cada renglon que referencian las formulas.
    agregar(null, renglon.clave);
  });

  const filaSaldoInicial = filaDe.saldoInicialCelda;
  const columnaSaldoInicial = XLSXStyle.utils.encode_col(COLUMNAS_FIJAS);

  renglones.forEach((renglon) => {
    if (!renglon.valores) return;
    const fila = filaDe[renglon.clave];
    const estilos = estilosDeRenglon(renglon);
    const celdas = columnas.map((columna, i) => {
      const col = letra(i);
      const valor = renglon.valores[i];

      if (renglon.tipo === "total") {
        const formula = formulaDeTotal(renglon.componentes || [], filaDe, col);
        return formula ? celdaFormula(formula, valor, estilos.dinero) : celdaMoneda(valor, estilos.dinero);
      }

      if (renglon.clave === "rem_remanente") {
        const anterior = i === 0 ? `$${columnaSaldoInicial}$${filaSaldoInicial}` : `${letra(i - 1)}${fila}`;
        return celdaFormula(
          `${anterior}+${col}${filaDe.tot_ingresos}-${col}${filaDe.tot_egresos}`,
          valor,
          estilos.dinero
        );
      }
      if (renglon.clave === "rem_saldoInicial" || renglon.clave === "rem_disponible") {
        return celdaFormula(`${col}${filaDe.rem_remanente}`, valor, estilos.dinero);
      }
      if (renglon.clave === "rem_diferencia") {
        if (valor === null || valor === undefined) return { v: "", s: estilos.dinero };
        const estilo =
          Math.abs(Number(valor)) >= 0.01
            ? { ...estilos.dinero, font: { bold: true, color: { rgb: "A8071A" } } }
            : estilos.dinero;
        return celdaFormula(`${col}${filaDe.rem_remanente}-${col}${filaDe.rem_bancos}`, valor, estilo);
      }

      if (valor === null || valor === undefined) return { v: "", s: estilos.dinero };
      return celdaMoneda(valor, estilos.dinero);
    });
    aoa[fila - 1] = [
      celdaCuenta(textoCuentas(renglon), estilos.etiqueta),
      celdaTexto(renglon.observaciones || "", estilos.etiqueta),
      celdaTexto(renglon.etiqueta, estilos.etiqueta),
      ...celdas,
    ];
  });

  // Por si algun renglon con valores no llego a la segunda pasada.
  aoa.forEach((fila, i) => {
    if (fila === null) aoa[i] = [];
  });

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = [{ wch: 16 }, { wch: 40 }, { wch: 44 }, ...columnas.map(() => ({ wch: 15 }))];
  // Sin autofiltro: con subtotales intercalados, filtrar haria que los totales dejaran
  // de corresponder a lo que se ve.
  return hoja;
};

const COLUMNAS_DETALLE = [
  { titulo: "Fecha", tipo: "fecha", ancho: 12 },
  { titulo: "Origen", tipo: "texto", ancho: 12 },
  { titulo: "Renglon", tipo: "texto", ancho: 38 },
  { titulo: "Documento", tipo: "identificador", ancho: 16 },
  { titulo: "Cliente / Proveedor", tipo: "texto", ancho: 36 },
  { titulo: "Cuenta contable", tipo: "texto", ancho: 32 },
  { titulo: "Poliza", tipo: "texto", ancho: 18 },
  { titulo: "Concepto", tipo: "texto", ancho: 42 },
  { titulo: "Monto", tipo: "moneda", ancho: 16 },
];

const construirHojaDetalle = ({ movimientos, renglones, empresaNombre, descripcionFiltros }) => {
  const etiquetas = new Map(renglones.map((r) => [r.clave, r.etiqueta]));
  const filas = (movimientos || []).map((m) => [
    fechaCelda(m.fecha),
    texto(ETIQUETA_ORIGEN[m.origen] || m.origen || ""),
    texto(etiquetas.get(m.clave) || m.clave),
    identificador(m.documento || ""),
    texto(m.tercero || ""),
    texto(m.cuenta || ""),
    texto(m.poliza || ""),
    texto(m.concepto || ""),
    moneda(m.monto),
  ]);
  // Sin fila de total: el detalle mezcla ingresos, egresos e informativos y su suma no
  // significa nada.
  return construirHojaTabla(
    { columnas: COLUMNAS_DETALLE, filas, totales: null },
    { titulo: "Detalle de movimientos", empresaNombre, descripcionFiltros }
  );
};

export const descargarExcel = ({ datos, columnas, renglones, descripcionFiltros }) => {
  const empresaNombre = nombreEmpresa(datos.empresa);
  const hojaFlujo = construirHojaFlujo({
    columnas,
    renglones,
    saldoInicial: datos.totales && datos.totales.saldoInicial,
    empresaNombre,
    descripcionFiltros,
  });
  const hojaDetalle = construirHojaDetalle({
    movimientos: datos.movimientos,
    renglones,
    empresaNombre,
    descripcionFiltros,
  });
  descargarLibro(
    [
      { nombre: "Flujo de Efectivo", hoja: hojaFlujo },
      { nombre: "Detalle", hoja: hojaDetalle },
    ],
    `Flujo_De_Efectivo_Diario_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`
  );
};
